import VerifiedIcon from "@mui/icons-material/Verified";
import { Box } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useParams } from "react-router-dom";
import CustomDataGridV2 from "../../../../components/CustomDataGridV2";
import Flex from "../../../../components/Flex";
import Text from "../../../../components/Text";
import ToggleSwitch from "../../../../components/ToggleSwitch";
import { useUsersList } from "../../../../queries/use-get-users";
import { useStatusUsers } from "../../../../queries/use-status-users";
import {
  DEFAULT_PAGE_SIZE,
  DGT_Pagination,
  applyDefaultColumnSettings,
  getAge,
} from "../../../../utils/utils";
import { GridSortItem } from "@mui/x-data-grid";

interface LocationUserProps {
  userUuid: string | undefined;
  searchKey: string;
  xTENANTID: string | null | undefined;
}

const LocationStaffUser = ({
  userUuid,
  searchKey,
  xTENANTID,
}: LocationUserProps) => {
  const [pagination, setPagination] = useState<DGT_Pagination>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);

  const { data: UsersList, refetch } = useUsersList({
    gridSorting,
    locationUuid: userUuid,
    providerGroupId: xTENANTID ?? undefined,
    pagination: {
      page: pagination.page,
      pageSize: pagination.pageSize,
    },
    searchKey,
  });

  const COLUMNS = applyDefaultColumnSettings<any>([
    // {
    //   field: "uuid",
    //   flex: 0.4,
    //   minWidth: 90,
    //   headerName: "User ID",
    //   sortable: false,
    //   renderCell: ({ row }) => {
    //     return <Text>{row?.uuid?.split("-")[0]?.toUpperCase()}</Text>;
    //   },
    // },
    {
      field: "userInfo",
      flex: 0.8,
      minWidth: 260,
      headerName: "Name",
      renderCell: ({ row }) => {
        return (
          <Box>
            <Flex gap={"20px"}>
              <Flex gap={0.5}>
                <Text fontWeight={"medium"} marginBottom={"3px"}>
                  {`${row?.firstName} ${row?.lastName} `}
                </Text>
                <Text variant="subtitle2">{`(${getAge(row?.birthDate)} yrs-${
                  row?.gender ?? ""
                })`}</Text>
              </Flex>
            </Flex>
          </Box>
        );
      },
      sortable: false,
    },
    {
      field: "role",
      flex: 0.4,
      minWidth: 140,
      headerName: "Role",
      sortable: false,
      renderCell: ({ row }) => row?.role ?? "-",
    },
    {
      field: "phone",
      flex: 0.5,
      minWidth: 120,
      headerName: "Contact",
      sortable: false,
    },
    {
      field: "email",
      flex: 0.9,
      minWidth: 240,
      headerName: "Email",
      renderCell: ({ row }) => (
        <Flex alignItems={"center"} gap={"4px"}>
          <VerifiedIcon
            color={row?.emailVerified ? "success" : "disabled"}
            fontSize="small"
          />
          <Text>{row?.email}</Text>
        </Flex>
      ),
      sortable: false,
    },
    {
      field: "lastLogin",
      flex: 0.6,
      minWidth: 160,
      headerName: "Last Sign In",
      sortable: false,
    },
    {
      field: "active",
      flex: 0.4,
      headerName: "Status",
      sortable: false,
      width: 150,
      renderCell: ({ row, value }) => (
        <ToggleSwitch
          id={row?.uuid?.toString()}
          checked={value}
          onConfirm={(v, id) => toggleStatus(v, id)}
        />
      ),
    },
  ]);

  const params = useParams();

  const { mutateAsync: statusChange } = useStatusUsers();
  const queryClient = useQueryClient();

  function toggleStatus<T>(value: boolean, id: T) {
    return statusChange({
      userId: id?.toString() ?? "",
      status: value,
      xTENANTID: params.providerGroupId,
    }).then((res) => {
      queryClient?.invalidateQueries({
        queryKey: ["provider-group-location-profile"],
      });
      refetch();
      return res;
    });
  }

  return (
    <Box sx={{ height: "calc(100vh - 7rem - 200px)" }}>
      <CustomDataGridV2
        searchString={searchKey}
        getRowId={(row) => row.uuid}
        rows={UsersList?.data.content ?? []}
        columns={COLUMNS}
        rowHeight={70}
        paginationModel={pagination}
        onPaginationModelChange={setPagination}
        onSortModelChange={setGridSorting}
        disableRowSelectionOnClick
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
        }
      />
    </Box>
  );
};

export default LocationStaffUser;
